/**
 * Planner state.
 *
 * State holds only what the user chose; everything else — required level,
 * costs, diffs — is derived on read. The spec is explicit about not duplicating
 * derived values into state, and this is where that rule is enforced.
 */
import { useCallback, useMemo, useReducer } from "react";
import type {
  CraftableWeapon,
  CraftingSocketKey,
  CraftingPlannerState,
  PlannerMode,
  WeaponConfiguration,
} from "@/lib/crafting/types";
import { SOCKET_ORDER } from "@/lib/crafting/types";
import {
  calculateCost,
  diffConfigurations,
  findMissingSelections,
  highestRequirementComponent,
  isConfigurationAvailable,
  isEmptySocketComponent,
  requiredWeaponLevel,
  resolveSelection,
  summarizeProgression,
} from "@/lib/crafting/engine";
import { DEFAULT_PROGRESSION_ESTIMATE } from "@/lib/crafting/provisional";

/** No level/percent actions: the weapon level is not user-editable. It comes
 *  from the signed-in account's crafted weapon, or is 0 for a new shape. */
type Action =
  | { type: "select"; key: CraftingSocketKey; plugHash: number }
  | { type: "clear"; key: CraftingSocketKey }
  | { type: "reset" }
  | { type: "load"; state: Partial<CraftingPlannerState> };

function reducer(state: CraftingPlannerState, action: Action): CraftingPlannerState {
  switch (action.type) {
    case "select":
      return {
        ...state,
        plannedConfiguration: { ...state.plannedConfiguration, [action.key]: action.plugHash },
      };
    case "clear": {
      const next = { ...state.plannedConfiguration };
      delete next[action.key];
      return { ...state, plannedConfiguration: next };
    }
    case "reset":
      // Back to the real weapon in reshape mode; empty when shaping new.
      return { ...state, plannedConfiguration: { ...(state.currentConfiguration ?? {}) } };
    case "load":
      return { ...state, ...action.state };
    default:
      return state;
  }
}

export interface PlannerOptions {
  weapon: CraftableWeapon;
  mode?: PlannerMode;
  currentConfiguration?: WeaponConfiguration;
  currentWeaponLevel?: number;
  currentLevelPercent?: number;
  initialPlan?: WeaponConfiguration;
}

/**
 * The default plan when shaping a new weapon: the cheapest level-0 option in
 * each column, skipping empty-socket placeholders. It gives the user a
 * complete, buildable weapon to edit rather than an empty screen. Memento is
 * left unset because it is genuinely optional.
 */
function defaultConfiguration(weapon: CraftableWeapon): WeaponConfiguration {
  const config: WeaponConfiguration = {};
  for (const socket of weapon.sockets) {
    if (socket.key === "memento") continue;
    const real = socket.options.filter((o) => !isEmptySocketComponent(o));
    const pool = real.length > 0 ? real : socket.options;
    const first = pool.find((o) => o.requiredLevel === 0) ?? pool[0];
    if (first) config[socket.key] = first.plugHash;
  }
  return config;
}

export function usePlanner(options: PlannerOptions) {
  const { weapon } = options;

  const [state, dispatch] = useReducer(reducer, undefined, (): CraftingPlannerState => {
    const mode = options.mode ?? "new-shape";
    const base =
      options.initialPlan ??
      options.currentConfiguration ??
      (mode === "new-shape" ? defaultConfiguration(weapon) : {});
    return {
      selectedWeaponHash: weapon.recipeHash,
      mode,
      currentConfiguration: options.currentConfiguration,
      currentWeaponLevel: options.currentWeaponLevel ?? 0,
      currentLevelPercent: options.currentLevelPercent ?? 0,
      plannedConfiguration: { ...base },
    };
  });

  const select = useCallback(
    (key: CraftingSocketKey, plugHash: number) => dispatch({ type: "select", key, plugHash }),
    []
  );
  const clear = useCallback((key: CraftingSocketKey) => dispatch({ type: "clear", key }), []);
  const reset = useCallback(() => dispatch({ type: "reset" }), []);

  // --- derived ---------------------------------------------------------------

  const derived = useMemo(() => {
    const selected = resolveSelection(weapon, state.plannedConfiguration);
    const required = requiredWeaponLevel(selected);
    const progression = summarizeProgression(
      selected,
      state.currentWeaponLevel,
      state.currentLevelPercent,
      DEFAULT_PROGRESSION_ESTIMATE
    );

    // In new-shape mode the "base" is what you can build immediately: the plan
    // restricted to components already unlocked at the current level.
    const shapeableNow: WeaponConfiguration = {};
    for (const s of selected) {
      if (s.component.requiredLevel <= state.currentWeaponLevel) {
        shapeableNow[s.key] = s.component.plugHash;
      }
    }
    const base = state.mode === "owned-reshape" ? state.currentConfiguration : shapeableNow;

    return {
      selected,
      requiredWeaponLevel: required,
      blame: highestRequirementComponent(selected),
      progression,
      /** What the plan is compared against: the real weapon in reshape mode,
       *  or the subset buildable at the current level when shaping new. */
      base,
      changes: diffConfigurations(weapon, base, state.plannedConfiguration),
      cost: calculateCost({
        weapon,
        mode: state.mode,
        baseConfiguration: base,
        plannedConfiguration: state.plannedConfiguration,
      }),
      availableNow: isConfigurationAvailable(selected, state.currentWeaponLevel),
      missing: findMissingSelections(weapon, state.plannedConfiguration),
      /** Columns the user has not filled in yet. */
      unfilled: SOCKET_ORDER.filter(
        (key) =>
          key !== "memento" &&
          weapon.sockets.some((s) => s.key === key) &&
          state.plannedConfiguration[key] === undefined
      ),
      isDirty:
        JSON.stringify(state.plannedConfiguration) !==
        JSON.stringify(state.currentConfiguration ?? {}),
    };
  }, [weapon, state]);

  return { state, derived, select, clear, reset };
}

export type Planner = ReturnType<typeof usePlanner>;
